import { useMemo, useState } from 'react';

import { Card, CardHeader } from '../ui/Card';
import { Badge, Pill } from '../ui/Badge';
import { SearchInput } from '../ui/Field';
import { EmptyState } from '../ui/AsyncBoundary';
import { formatAttribute, attributeTone, humanKeyFallback } from '../../lib/attributes';

/**
 * Everything this particular device is reporting.
 *
 * The point of the panel: a person looking at a reefer truck should see its
 * door state and compressor here without anyone having built a reefer screen.
 * The values come from the device's own attribute snapshot; the labels and
 * units come from the catalog, and anything the catalog has not seen yet is
 * still shown, under a readable version of its key.
 */
export function AttributePanel({ attributes, catalog, title = 'All readings', subtitle }) {
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    if (!attributes) return [];
    const byKey = new Map((catalog || []).map((attribute) => [attribute.key, attribute]));
    const needle = query.trim().toLowerCase();

    return Object.entries(attributes)
      .map(([key, value]) => {
        const meta = byKey.get(key) || {
          key,
          label: humanKeyFallback(key),
          unit: null,
          type: typeof value === 'boolean' ? 'boolean' : typeof value === 'number' ? 'number' : 'string',
          group: 'Reported by device',
          source: 'EXTRA',
        };
        return { meta, value };
      })
      .filter(
        ({ meta }) =>
          !needle ||
          meta.label.toLowerCase().includes(needle) ||
          meta.key.toLowerCase().includes(needle)
      )
      .sort((a, b) => {
        // Device-specific readings first — they are the ones nobody has seen
        // before and the reason to open this panel at all.
        if (a.meta.source !== b.meta.source) return a.meta.source === 'EXTRA' ? -1 : 1;
        return a.meta.label.localeCompare(b.meta.label);
      });
  }, [attributes, catalog, query]);

  const extraCount = rows.filter((row) => row.meta.source === 'EXTRA').length;

  return (
    <Card>
      <CardHeader
        title={title}
        subtitle={
          subtitle ??
          (extraCount
            ? `${rows.length} attributes, ${extraCount} specific to this hardware`
            : `${rows.length} attributes`)
        }
        icon="table"
        actions={
          rows.length > 8 ? (
            <SearchInput value={query} onChange={setQuery} placeholder="Find" className="w-40" />
          ) : null
        }
      />

      <div className="p-4">
        {!rows.length ? (
          <EmptyState
            icon="chip"
            title="Nothing reported yet"
            description="Readings appear here as soon as the device sends its first packet."
          />
        ) : (
          <dl className="grid gap-x-6 gap-y-0 sm:grid-cols-2">
            {rows.map(({ meta, value }) => {
              const tone = attributeTone(meta, value);
              return (
                <div
                  key={meta.key}
                  className="flex items-baseline justify-between gap-3 border-b border-line py-2 last:border-0"
                >
                  <dt className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[13px] text-ink-2">{meta.label}</span>
                    {meta.source === 'EXTRA' && (
                      <Pill title={`Reported by ${meta.models?.join(', ') || 'this device'}`}>dev</Pill>
                    )}
                  </dt>
                  <dd className="shrink-0 text-[13.5px] font-medium text-ink tabular-nums">
                    {tone ? (
                      <Badge tone={tone} size="sm">
                        {formatAttribute(meta, value)}
                      </Badge>
                    ) : (
                      formatAttribute(meta, value)
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        )}
      </div>
    </Card>
  );
}
